import { parsePrice, type PriceInput } from '@/shared/utils/priceFormatter';
import {
  buildFacebookPdpAttributes,
  commonFacebookEventsUtil,
} from '@/shared/features/analytics/utils/facebookCommonAttributes';
import { getUserInfo } from '@/shared/features/analytics/utils/commonAttributes';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '@/features/pdp/presentation/viewModels/pdp.vm';

function getWishlistItemCountAfter(): number | undefined {
  const userInfo = getUserInfo();
  return userInfo?.wishlistCount != null ? Number(userInfo.wishlistCount) : undefined;
}

export interface FacebookAddToWishlistParams {
  designId: number | string | null | undefined;
  designName: string | null | undefined;
  designCategory: string | null | undefined;
  price: PriceInput;
  /** Customization / variant id. Falls back to designId when omitted. */
  variantId?: number | string | null;
}

/**
 * Meta Pixel - AddToWishlist (listing / lightweight product payloads).
 * Common Facebook user/page attributes are merged by `commonFacebookEventsUtil`.
 */
export function facebookAddToWishlist({
  designId,
  designName,
  designCategory,
  price,
  variantId,
}: FacebookAddToWishlistParams): void {
  const parsedPrice =
    price != null && price !== '' ? parsePrice(String(price)) : 0;
  const contentId =
    designId != null && designId !== '' ? String(designId) : undefined;
  const resolvedVariantId =
    variantId != null && variantId !== '' ? String(variantId) : contentId;

  commonFacebookEventsUtil('AddToWishlist', {
    content_names: designName ? [designName] : [],
    content_category: designCategory ?? '',
    content_ids: contentId ? [contentId] : [],
    content_type: 'product',
    contents: contentId ? [{ id: contentId, quantity: 1 }] : [],
    value: parsedPrice,
    currency: 'INR',
    design_id: contentId,
    variant_id: resolvedVariantId,
    wishlist_item_count_after: getWishlistItemCountAfter(),
  });
}

/**
 * Meta Pixel - AddToWishlist from PDP (same product attrs as AddToCart / ViewContent).
 */
export function facebookAddToWishlistFromPdp(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): void {
  commonFacebookEventsUtil('AddToWishlist', {
    ...buildFacebookPdpAttributes(product, vm, selectedSizeDisplay),
    wishlist_item_count_after: getWishlistItemCountAfter(),
  });
}
